import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useAuthorizedIntegrationContext } from '@shared/contexts/pluginContexts/AuthorizedIntegrationContext';
import { useEmailThreadContext } from '@shared/contexts/pluginContexts/EmailThreadContext';
import { useCallback, useRef } from 'react';
import { pickOpenEmailMessageId } from './pickOpenEmailMessageId';
import { waitForLoadedValue } from './waitForLoadedValue';

type UseGetOpenEmailMessageIdInput = {
  isLoadingMessages: boolean;
  messages: { id: string; userId?: string }[];
};

type StoredOpenEmail = {
  key: string;
  messageId: Promise<string | undefined>;
};

export function useGetOpenEmailMessageId({
  isLoadingMessages,
  messages,
}: UseGetOpenEmailMessageIdInput): () => Promise<string | undefined> {
  const { defaultOrgId, me } = useMeContext();
  const { authorized } = useAuthorizedIntegrationContext();
  const { isComposeMode, storeOpenEmail, thisItem } = useEmailThreadContext();
  const latest = {
    canStoreOpenEmail: authorized && !isComposeMode,
    itemKey: thisItem?.id && `${defaultOrgId}:${thisItem.id}`,
    loading: isLoadingMessages,
    scannedMessageId: pickOpenEmailMessageId(messages, me?.id),
    storeOpenEmail,
  };
  const latestRef = useRef(latest);
  latestRef.current = latest;
  const storedRef = useRef<StoredOpenEmail | undefined>(undefined);

  return useCallback(async () => {
    const scannedMessageId = await waitForLoadedValue({
      getSnapshot: () => ({
        value: latestRef.current.scannedMessageId,
        loading: latestRef.current.loading,
      }),
    });
    if (scannedMessageId) return scannedMessageId;

    const { canStoreOpenEmail, itemKey, storeOpenEmail } = latestRef.current;
    if (!canStoreOpenEmail || !itemKey || !storeOpenEmail) return undefined;
    if (storedRef.current?.key !== itemKey) {
      const stored: StoredOpenEmail = {
        key: itemKey,
        messageId: storeOpenEmail().catch(() => {
          if (storedRef.current === stored) storedRef.current = undefined;
          return undefined;
        }),
      };
      storedRef.current = stored;
    }
    return storedRef.current.messageId;
  }, []);
}
